import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flower2, Landmark, Sparkles, MapPin, Quote } from "lucide-react";
import api from "@/lib/api";
import Shell from "@/components/Shell";
import ExperienceGrid from "@/components/ExperienceGrid";

const HERO = "https://images.unsplash.com/photo-1650341278999-d1b5142cfe30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const AWAKENING = [
  { title: "Silent Vipassana", desc: "10 days of noble silence to reset the mind completely.", tag: "spiritual-awakening" },
  { title: "Sound Healing & Breathwork", desc: "Himalayan singing bowls, breath and river-side stillness.", tag: "spiritual-awakening" },
  { title: "Yoga & Meditation", desc: "Daily practice by the Ganga with certified teachers.", tag: "spiritual-awakening" },
  { title: "Meditation Pilgrimage", desc: "Walk the paths of the Buddha and ancient sages.", tag: "spiritual-awakening" },
];

const PLACES = [
  { name: "Rishikesh", note: "Yoga capital · Ganga Aarti" },
  { name: "Varanasi", note: "Oldest living city · Ghats" },
  { name: "Kedarnath", note: "Himalayan Jyotirlinga" },
  { name: "Amritsar", note: "The Golden Temple" },
  { name: "Bodh Gaya", note: "Where the Buddha awakened" },
  { name: "Tawang", note: "Cliff-top monastery" },
];

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.05 } }) };

const OSHO_FACTS = [
  "Born Chandra Mohan Jain in 1931 in Kuchwada, Madhya Pradesh — later known as Acharya Rajneesh, Bhagwan Shree Rajneesh, and finally Osho.",
  "His discourses were compiled into more than 600 books, translated into over 60 languages — one of the largest bodies of spoken spiritual teaching in the world.",
  "He created 'Dynamic Meditation' — an active, cathartic technique designed for the restless modern mind that can't simply sit still.",
  "His vision of 'Zorba the Buddha' fused living life fully — love, work, celebration — with deep inner meditation.",
  "The OSHO International Meditation Resort in Pune still draws seekers from across the world every year.",
];

const OSHO_QUOTES = [
  "Truth is not something outside to be discovered — it is something inside to be realised.",
  "Life begins where fear ends.",
  "Drop the idea of becoming someone, because you are already a masterpiece.",
  "The moment you accept yourself, you become beautiful.",
];

export default function Spiritual() {
  const [awakening, setAwakening] = useState([]);
  const [places, setPlaces] = useState([]);

  useEffect(() => {
    api.get("/experiences?category=spiritual-awakening").then((r) => setAwakening(r.data)).catch(() => {});
    api.get("/experiences?category=spiritual-places").then((r) => setPlaces(r.data)).catch(() => {});
  }, []);

  const hidden = [...places, ...awakening].filter((e) => e.mode === "discovery");

  return (
    <Shell>
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Temple at dawn" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/30 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 md:py-36">
          <motion.span initial="hidden" animate="show" variants={fade} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white">
            <Flower2 size={14} /> Spiritual Tourism
          </motion.span>
          <motion.h1 custom={1} initial="hidden" animate="show" variants={fade} className="font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-6 text-white max-w-3xl">
            Travel inward,<br /><span className="italic font-normal">as you travel across India.</span>
          </motion.h1>
          <motion.p custom={2} initial="hidden" animate="show" variants={fade} className="mt-6 text-lg font-light text-white/90 max-w-2xl leading-relaxed">
            From silent retreats in the Himalayas to the fire ceremonies of Varanasi — curated journeys of awakening, devotion and stillness.
          </motion.p>
        </div>
      </section>

      {/* AWAKENING TOPICS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Spiritual Awakening</span>
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-2">Paths to yourself</h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {AWAKENING.map((a, i) => (
            <motion.div key={a.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
              <Link to={`/explore?category=${a.tag}`} data-testid={`awakening-${i}`}
                className="group block h-full rounded-2xl border border-primary/10 bg-card p-6 hover:-translate-y-1 transition-transform">
                <Sparkles size={20} className="text-accent" />
                <h3 className="font-semibold text-lg mt-4 text-foreground">{a.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{a.desc}</p>
                <span className="inline-flex items-center gap-1 text-accent text-sm font-semibold mt-4 group-hover:gap-2 transition-all">Explore <ArrowRight size={14} /></span>
              </Link>
            </motion.div>
          ))}
        </div>
        {awakening.length > 0 && (
          <div className="mt-12"><ExperienceGrid items={awakening} prefix="awakening-exp" /></div>
        )}
      </section>

      {/* SPIRITUAL PLACES */}
      <section className="bg-secondary/40 border-y border-primary/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Spiritual Places</span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-2">Sacred locations</h2>
          <p className="text-muted-foreground mt-3 max-w-xl text-lg">The temples, ghats and monasteries that have drawn seekers for centuries.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {PLACES.map((p) => (
              <div key={p.name} className="flex items-center gap-2 rounded-full border border-primary/20 bg-card px-4 py-2" data-testid={`place-${p.name.toLowerCase()}`}>
                <Landmark size={15} className="text-accent" />
                <span className="text-sm font-semibold text-foreground">{p.name}</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">· {p.note}</span>
              </div>
            ))}
          </div>
          {places.length > 0 && (
            <div className="mt-12"><ExperienceGrid items={places} prefix="place-exp" /></div>
          )}
        </div>
      </section>

      {/* HIDDEN SPIRITUAL LOCATIONS */}
      {hidden.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Off the pilgrim trail</span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-2">Hidden spiritual locations</h2>
          <p className="text-muted-foreground mt-3 max-w-xl text-lg flex items-center gap-1"><MapPin size={16} /> Quiet, lesser-known places — enquire to plan your own retreat.</p>
          <div className="mt-10"><ExperienceGrid items={hidden} prefix="hidden-spiritual" /></div>
        </section>
      )}

      {/* OSHO — A BENCH IN THE HIMALAYAS */}
      <section className="bg-secondary/40 border-y border-primary/10" data-testid="osho-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="grid gap-10 lg:grid-cols-2 items-center">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
              <div className="relative rounded-[2rem] overflow-hidden border border-primary/10 shadow-xl">
                <img src="/osho.jpg" alt="Founder in conversation with a Himalayan mystic" className="w-full h-full object-cover" data-testid="osho-image" />
              </div>
              <p className="text-sm text-muted-foreground mt-3 flex items-center gap-1.5"><MapPin size={14} /> A chance conversation with a wandering mystic in the Himalayan hills — the kind of meeting a Zatayoo journey is made of.</p>
            </motion.div>

            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Wisdom on the road</span>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-2">Sitting with a mystic — the world of Osho</h2>
              <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
                Some of the deepest travel happens sitting still. Osho — one of India's most provocative and beloved mystics — spent a lifetime pointing seekers back to the one place they never look: within.
              </p>

              <ul className="mt-6 space-y-3">
                {OSHO_FACTS.map((f, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`osho-fact-${i}`}>
                    <Sparkles size={16} className="text-accent shrink-0 mt-1" />
                    <span className="text-sm text-foreground/85 leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Quotes on the reality of life */}
          <div className="mt-14">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">On the reality of life</span>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {OSHO_QUOTES.map((q, i) => (
                <motion.blockquote key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                  className="relative rounded-2xl border border-primary/10 bg-card p-6" data-testid={`osho-quote-${i}`}>
                  <Quote size={20} className="text-accent/50" />
                  <p className="font-display text-xl text-foreground mt-3 leading-snug italic">"{q}"</p>
                  <footer className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4">— Osho</footer>
                </motion.blockquote>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 pb-24">
        <div className="rounded-[2rem] bg-primary text-primary-foreground p-10 md:p-16">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight max-w-2xl">Not sure where to begin your journey?</h2>
          <p className="mt-4 text-primary-foreground/70 text-lg max-w-lg">Let our AI planner design a spiritual itinerary around your intent, dates and budget.</p>
          <Link to="/planner" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-4 font-semibold mt-8 hover:-translate-y-px transition-transform" data-testid="spiritual-planner-cta">
            Plan my journey <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </Shell>
  );
}
